import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {useQueryClient} from '@tanstack/react-query'
import {Button} from 'primereact/button'
import {Dialog} from 'primereact/dialog'
import {Divider} from 'primereact/divider'
import React, {
    forwardRef,
    useCallback,
    useImperativeHandle,
    useRef,
} from 'react'
import {SystemRole} from '../../../Constants'
import {useUserHospital} from '../../../context/hospital/UserHospitalContext'
import {QueryKeyTypes} from '../../../query-manager/QueryKeyTypes'
import {
    QueryCRUDTypes,
    QueryKeyManager,
} from '../../../query-manager/QueryKeysManager'
import {checkEmptyString, validEmail} from '../../../utils/CommonUtils'
import AddressForm from '../../address/AddressForm'
import {useAuth} from '../../authentication/AuthProvider'
import {component} from '../../form/Constant'
import {useHealthCareCRUD} from '../../healthcare/query-keys/HealthCareQueryManager'
import ImageUploader from '../../image/ImageUploader'
import PageContentWrapper from '../../layout/PageContentWrapper'
import StyledRequireText from '../../layout/StyledRequireText'
import {severityNotification} from '../../notification/NotificationConstant'
import {showNotification} from '../../notification/NotificationService'
import StyledDropdown from '../components/StyledDropdown'
import StyledInput from '../components/StyledInput'
import StyledInputMask from '../components/StyledInputMask'
import StyledHospitalOptions from '../styled-options/StyledHospitalOptions'
import StyledRoleOptions from '../styled-options/StyledRoleOptions'
import {hcpKeys, healthCareList, toPostDataKeys} from './Constants'

const HealthCarePractitionerForm = forwardRef((props, ref) => {
    useSignals()

    useImperativeHandle(ref, () => ({
        showForm: handleShowForm,
        hideForm: handleHideForm,
    }))

    //#region Context
    const {user} = useAuth()
    const queryClient = useQueryClient()
    const {currentHospital} = useUserHospital()
    //#endregion

    //#region State
    const visible = useSignal(false)
    const formData = useSignal({})
    const formRefs = useRef([])

    const handleShowForm = useCallback(data => {
        if (data && data?.id) {
            formData.value = {
                ...data,
            }
        }

        visible.value = true
    }, [])

    const handleHideForm = useCallback(() => {
        formData.value = {}
        visible.value = false
    }, [])
    //#endregion

    //#region CRUD  Function
    const {mutateAsync: createOrUpdateHealthCare} = useHealthCareCRUD(
        QueryCRUDTypes.create,
        user?.id,
    )

    const handleSubmit = async () => {
        let toPostData = {
            ...formData.value,
            systemRole: SystemRole.USER_WITH_ROLE,
        }

        toPostDataKeys?.map(key => {
            if (key === hcpKeys.address) {
                toPostData = {
                    ...toPostData,
                    ...formRefs.current[key]?.getValue(),
                }
            } else {
                toPostData[key] = formRefs.current[key]?.getValue()
            }
        })

        if (!toPostData?.hospitalId) {
            showNotification(
                severityNotification.error,
                'Please select hospital',
            )
            return
        }

        if (!toPostData?.roleId) {
            showNotification(severityNotification.error, 'Please select role')
            return
        }

        if (
            !toPostData?.firstName ||
            toPostData?.firstName?.length < 0 ||
            !!checkEmptyString(toPostData?.firstName)
        ) {
            showNotification(
                severityNotification.error,
                'First Name is required !!',
            )
            return
        }

        if (!toPostData?.email || toPostData?.email?.length < 0) {
            showNotification(severityNotification.error, 'Email is required !!')
            return
        }

        if (!validEmail(toPostData?.email)) {
            showNotification(severityNotification.error, 'Email is invalid !!')
            return
        }

        const hcpData = await createOrUpdateHealthCare(toPostData)

        if (hcpData?.id) {
            const {healthCareKeys} = QueryKeyManager(
                QueryKeyTypes.hcp,
                user?.id,
            )

            queryClient.invalidateQueries({
                queryKey: healthCareKeys.pattern(),
            })

            handleHideForm()
        }
    }
    //#endregion

    return (
        <Dialog
            id="blockscroll-sidebar"
            className="p-sidebar-full"
            visible={visible.value}
            onHide={handleHideForm}
            header="MEDICAL STAFF MEMBERS FORM"
            blockScroll
            fullScreen>
            <PageContentWrapper>
                <React.Fragment>
                    <div className="grid">
                        {/* PRE-CONDITION */}
                        <div className="col-4 relative">
                            <div className="sticky">
                                <h4 className="font-bold underline-60">
                                    Hospital Information
                                </h4>
                                <p>Select hospitals from the list below</p>
                            </div>
                        </div>
                        <div className="col-8">
                            <div className="mb-3">
                                {/* Component Header */}
                                <div className="flex align-items-center justify-content-between">
                                    <div>
                                        <h6 className="text-700 font-semibold mb-1">
                                            Hospital Options
                                        </h6>
                                    </div>

                                    <div>
                                        <StyledRequireText />
                                    </div>
                                </div>
                                {/* End Region */}
                                <StyledHospitalOptions
                                    ref={el =>
                                        (formRefs.current[hcpKeys.hospitalId] =
                                            el)
                                    }
                                    optionType={component.dropdownGroup}
                                    groupByIsNull={null}
                                    placeholder={'Please select hospital'}
                                    value={
                                        formData.value[hcpKeys.hospitalId]
                                            ? formData.value[hcpKeys.hospitalId]
                                            : currentHospital?.id
                                    }
                                />
                            </div>
                        </div>

                        <Divider type="dashed" />

                        <div className="col-4 relative">
                            <div className="sticky">
                                <h4 className="font-bold underline-60">
                                    Role Information
                                </h4>
                                <p>Select a role associated with this HCP</p>
                            </div>
                        </div>
                        <div className="col-8">
                            <div className="mb-3">
                                {/* Component Header */}
                                <div className="flex align-items-center justify-content-between">
                                    <div>
                                        <h6 className="text-700 font-semibold mb-1">
                                            Role Options
                                        </h6>
                                    </div>

                                    <div>
                                        <StyledRequireText />
                                    </div>
                                </div>
                                {/* End Region */}
                                <StyledRoleOptions
                                    ref={el =>
                                        (formRefs.current[hcpKeys.roleId] = el)
                                    }
                                    optionType={component.dropdown}
                                    groupByIsNull={null}
                                    placeholder={'Please select role'}
                                    value={formData.value[hcpKeys.roleId]}
                                />
                            </div>
                        </div>

                        <Divider type="dashed" />
                        {/* END REGION */}

                        {healthCareList?.map((item, index) => (
                            <React.Fragment key={item?.key}>
                                {/* Group Header */}
                                <div className="col-4 relative">
                                    <div className="sticky">
                                        <h4 className="font-bold underline-60">
                                            {item.label}
                                        </h4>
                                        <p>{item?.description}</p>
                                    </div>
                                </div>
                                {/* End region */}

                                {/* Components */}
                                {item?.children?.length > 0 && (
                                    <div className="col-8">
                                        {item?.children?.map(child => (
                                            <div
                                                key={child?.key}
                                                className="mb-3">
                                                {/* Component Header */}
                                                <div className="flex align-items-center justify-content-between">
                                                    <div>
                                                        <h6 className="text-700 font-semibold mb-1">
                                                            {child?.label}
                                                        </h6>
                                                    </div>

                                                    <div>
                                                        {child?.required && (
                                                            <StyledRequireText />
                                                        )}
                                                    </div>
                                                </div>
                                                {/* End Region */}

                                                {/* Render Child Components */}
                                                {child?.component ===
                                                    component.inputText && (
                                                    <StyledInput
                                                        ref={el =>
                                                            (formRefs.current[
                                                                child.key
                                                            ] = el)
                                                        }
                                                        value={
                                                            formData.value[
                                                                child?.key
                                                            ] || ''
                                                        }
                                                        placeholder={
                                                            child?.placeholder
                                                        }
                                                        keyfilter={
                                                            child?.keyFilter
                                                                ? child.keyFilter
                                                                : null
                                                        }
                                                        transformType={
                                                            child?.transformType
                                                        }
                                                    />
                                                )}

                                                {child?.component ===
                                                    component.inputMask && (
                                                    <StyledInputMask
                                                        ref={el =>
                                                            (formRefs.current[
                                                                child.key
                                                            ] = el)
                                                        }
                                                        value={
                                                            formData.value[
                                                                child?.key
                                                            ] || ''
                                                        }
                                                        placeholder={
                                                            child?.placeholder
                                                        }
                                                    />
                                                )}

                                                {child?.component ===
                                                    component.fileUpload && (
                                                    <ImageUploader
                                                        id={child.key}
                                                        ref={el =>
                                                            (formRefs.current[
                                                                child.key
                                                            ] = el)
                                                        }
                                                        initialImage={
                                                            formData.value[
                                                                child?.key
                                                            ]
                                                        }
                                                        multiple={
                                                            !!item?.multiple
                                                        }
                                                        extraData={
                                                            !!item?.extraData
                                                        }
                                                    />
                                                )}

                                                {child?.component ===
                                                    component.dropdown && (
                                                    <React.Fragment>
                                                        <StyledDropdown
                                                            ref={el =>
                                                                (formRefs.current[
                                                                    child.key
                                                                ] = el)
                                                            }
                                                            value={
                                                                formData.value[
                                                                    child?.key
                                                                ] ||
                                                                child?.defaultValue
                                                            }
                                                            options={
                                                                child?.options ||
                                                                []
                                                            }
                                                        />
                                                    </React.Fragment>
                                                )}

                                                {child?.component ===
                                                    component.address && (
                                                    <AddressForm
                                                        id={item.key}
                                                        ref={el =>
                                                            (formRefs.current[
                                                                child.key
                                                            ] = el)
                                                        }
                                                        className={'grid'}
                                                        childrenClassName={
                                                            'col-6'
                                                        }
                                                        defaultValue={
                                                            formData.value
                                                                ? {
                                                                      ...formData.value,
                                                                  }
                                                                : {}
                                                        }
                                                    />
                                                )}
                                                {/* End Region */}

                                                {/* DESCRIPTION */}
                                                {child?.description &&
                                                    (typeof child?.description ===
                                                    'string' ? (
                                                        <p>
                                                            {child?.description}
                                                        </p>
                                                    ) : (
                                                        child?.description
                                                    ))}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <Divider type="dashed" />
                            </React.Fragment>
                        ))}
                    </div>
                </React.Fragment>
                {/* Action Button */}
                <Button
                    className="col-12 h-3rem"
                    label="SUBMIT"
                    icon="pi pi-send"
                    onClick={() => handleSubmit()}
                />
            </PageContentWrapper>
        </Dialog>
    )
})

export default HealthCarePractitionerForm
